import { Card, CardContent } from "@/components/ui/card";
import { TimelineRuler } from "./Transport";
import WaveLane from "./WaveLane";
import LoopMarkers from "./LoopMarkers";
import SyncQuantizePanel from "./SyncQuantizePanel";
import { tintFor } from "./dawHelpers";

export default function TimelinePanel({
  channels, visibleTimelineLanes, selectedChannel, playheadPct, maxDur,
  recordingIdx, recordingMeters, onSelectChannel,
  loopEnabled, loopStart, loopEnd, onLoopRangeChange, onLoopReset,
  onOpenClipEditor, onDropFile, onDropFiles,
  engine, loadedCount, tempo, onSynced, onQuantized, onCleared,
  // iter-106 — drives the playhead-glow + lane sheen + empty-ghost anims.
  playing = false,
  // iter-197 — live per-channel peak history during play + record. Both
  // are accessor fns (kind, ch) → number[96]|null so we don't allocate
  // intermediate arrays in the parent for every channel every render.
  readLiveSamples = null,
  isClipFlashing = null,
  isClipBadgeActive = null,
  liveWaveRev = 0,
}) {
  // recordingIdx kept in the prop signature so the parent can still pass it
  // even though the bar that used it has moved to the sticky footer.
  void recordingIdx;
  // Track multi-file drops at the timeline level — only activates when the
  // user drops onto the gaps between lanes / the timeline header. Per-lane
  // drops bubble up here too but are short-circuited by WaveLane.onDrop's
  // event.stopPropagation via preventDefault.
  const handleTimelineDragOver = (e) => {
    if (!onDropFiles) return;
    const hasFile = Array.from(e.dataTransfer.items || []).some((it) => it.kind === "file");
    if (!hasFile) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleTimelineDrop = (e) => {
    if (!onDropFiles) return;
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    e.preventDefault();
    // Skip when a child lane already handled it (its onDrop will have removed
    // the items from the event by the time it bubbles).
    if (e.defaultPrevented && e.dataTransfer.files.length === 0) return;
    onDropFiles(null, files);
  };
  return (
    <Card className="glass-card rounded-3xl border-0 text-white">
      <CardContent className="p-4" onDragOver={handleTimelineDragOver} onDrop={handleTimelineDrop}>
        <TimelineRuler />
        {engine && (
          <SyncQuantizePanel
            engine={engine}
            selectedChannel={selectedChannel}
            loadedCount={loadedCount}
            tempo={tempo}
            onSynced={onSynced}
            onQuantized={onQuantized}
            onCleared={onCleared}
          />
        )}
        <div className="rounded-2xl border border-white/[0.07] bg-black/35 p-3"
          style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 0 rgba(0,0,0,0.4)" }}>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold tracking-tight">Timeline Arrangement</h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">Bars 1–96</span>
          </div>
          <LoopMarkers
            enabled={loopEnabled}
            loopStart={loopStart}
            loopEnd={loopEnd}
            maxDur={maxDur}
            onChange={onLoopRangeChange}
            onReset={onLoopReset}
          />
          <div className="max-h-[420px] overflow-y-auto pr-1">
            {Array.from({ length: visibleTimelineLanes }).map((_, i) => (
              <WaveLane
                key={`timeline-lane-${i}`}
                index={i}
                selected={selectedChannel === i}
                channel={channels[i]}
                /* Primitive snapshots — see MixerPanel.jsx comment for context. */
                hasBuffer={!!channels[i]?.buffer}
                channelName={channels[i]?.name}
                waveformRev={channels[i]?.waveform}
                tint={tintFor(i)}
                playheadPct={playheadPct}
                onSelect={onSelectChannel}
                onOpenClipEditor={onOpenClipEditor}
                recordingMeter={recordingMeters?.[i]}
                onDropFile={onDropFile}
                playing={playing}
                liveRecSamples={readLiveSamples ? readLiveSamples("rec", i) : null}
                livePlaySamples={readLiveSamples ? readLiveSamples("play", i) : null}
                clipping={isClipFlashing ? isClipFlashing(i) : false}
                clippedRecently={isClipBadgeActive ? isClipBadgeActive(i) : false}
                liveWaveRev={liveWaveRev}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
